/**
 * Use Audit Logs Hook
 *
 * Custom hook for fetching audit logs with TanStack Query.
 * Handles caching, refetching, and error states automatically.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { fetchAuditLogs, fetchAuditLogById, fetchAuditLogSummary } from '@/lib/api';
import { useAuth } from '@/state/auth-context';
import type { AuditLog, AuditLogFilters, PaginatedAuditLogs, AuditLogSummary } from '@/types/audit-log';

/**
 * Use Audit Logs
 * Fetches paginated list of audit logs with filters
 *
 * @param filters - Filter parameters for the query
 * @returns TanStack Query result with audit logs data
 */
export function useAuditLogs(filters: AuditLogFilters): UseQueryResult<PaginatedAuditLogs> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: ['auditLogs', filters],
    queryFn: async () => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return fetchAuditLogs(filters, token);
    },
    staleTime: 30000, // 30 seconds - data is considered fresh for 30s
    gcTime: 300000, // 5 minutes - cache is garbage collected after 5 minutes
    retry: 1, // One retry — repeated retries pile abandoned queries onto Postgres
    refetchOnWindowFocus: false, // Tab-switching must not hammer the audit DB
    refetchOnReconnect: true, // Refetch when network reconnects
  });
}

/**
 * Use Audit Log by ID
 * Fetches a single audit log entry by its ID
 *
 * @param id - Audit log entry ID
 * @param enabled - Whether the query should run (default: true)
 * @returns TanStack Query result with single audit log data
 */
export function useAuditLogById(id: string | null, enabled = true): UseQueryResult<AuditLog> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: ['auditLog', id],
    queryFn: async () => {
      if (!id) {
        throw new Error('No audit log ID provided');
      }

      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return fetchAuditLogById(id, token);
    },
    enabled: enabled && id !== null, // Only run query if enabled and ID is provided
    staleTime: 60000, // 1 minute - individual logs change less frequently
    gcTime: 600000, // 10 minutes - keep in cache longer
    retry: 2,
  });
}

/**
 * Use Audit Log Summary
 * Fetches total count + severity breakdown for the current filters.
 * Call in parallel with useAuditLogs — renders table first, counts second.
 */
export function useAuditLogSummary(filters: AuditLogFilters): UseQueryResult<AuditLogSummary> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: ['auditLogSummary', filters],
    queryFn: async () => {
      const token = getIdToken();
      if (!token) throw new Error('No ID token available. Please log in again.');
      return fetchAuditLogSummary(filters, token);
    },
    staleTime: 300000, // 5 minutes — matches the server-side Redis TTL for counts
    gcTime: 600000,
    retry: 1, // One retry — repeated retries pile abandoned queries onto Postgres
    refetchOnWindowFocus: false, // Tab-switching must not re-fire the count query
    refetchOnReconnect: true,
  });
}
